/**
 * Farmers Pulse route manifest.
 *
 * `ROUTES` is a single array declaring every route the docket owns.
 * `routes.jsx` renders these as <Route>s; the standalone fork rewrites
 * each `path` to a root URL by stripping the "/farmers" prefix.
 */
import FarmersPulseHome from "@/pages/FarmersPulseHome";
import FarmersAbout from "@/pages/FarmersAbout";
import FarmersMine from "@/pages/FarmersMine";
import FarmersWeather from "@/pages/FarmersWeather";
import FarmersMap from "@/pages/FarmersMap";
import FarmersLocal from "@/pages/FarmersLocal";
import FarmersCommunity, { FarmersCommunityDetail } from "@/pages/FarmersCommunity";
import FarmersMarkets from "@/pages/FarmersMarkets";
import { FarmersFarms, FarmersFarmNew, FarmersFarmDetail } from "@/pages/FarmersFarms";
import { FarmersCrops, FarmersCropDetail, FarmersCalendar } from "@/pages/FarmersCrops";
import FarmersReport from "@/pages/FarmersReport";
import FarmersPestHeatmap from "@/pages/FarmersPestHeatmap";
import FarmersAsk from "@/pages/FarmersAsk";
import FarmersWebsitesList from "@/pages/FarmersWebsitesList";
import FarmersWebsiteWizard from "@/pages/FarmersWebsiteWizard";
import FarmersWebsiteImport from "@/pages/FarmersWebsiteImport";
import FarmersWebsiteEditor from "@/pages/FarmersWebsiteEditor";
import FarmerSitePublic from "@/pages/FarmerSitePublic";
import FarmersInventory from "@/pages/FarmersInventory";
import FarmersPulseAlmanac from "@/pages/FarmersPulseAlmanac";
import FarmersGeoTree from "@/pages/FarmersGeoTree";
import FarmersNews from "@/pages/FarmersNews";
import FarmersToday from "@/pages/FarmersToday";
import FarmersBilling from "@/pages/FarmersBilling";
import FarmersBillingSuccess from "@/pages/FarmersBillingSuccess";
import FarmersRevenueAdmin from "@/pages/FarmersRevenueAdmin";
import FarmersOrders from "@/pages/FarmersOrders";
import {
  FarmersNetwork, FarmersProducts, FarmersDeals, FarmersSurplus,
  FarmersMarketplaceHub, FarmersFarmStand, FarmersBuyers, FarmersNeeds,
  FarmersFarmStay, FarmersFarmDining, FarmersExperiences, FarmersEvents,
  FarmersAlmanac as FarmersAlmanacHub, FarmersLearning, FarmersInnovation,
  FarmersFarmHelp, FarmersJobs, FarmersServices, FarmersCooperatives,
  FarmersTransport, FarmersStorage,
  FarmersDocuments, FarmersFinance, FarmersJournal, FarmersReports,
} from "@/pages/FarmersModules";
import {
  FarmersAlerts, FarmersLivestock, FarmersKnowledge,
  FarmersResearch, FarmersGovernment, FarmersMarketplace,
  FarmersEquipment, FarmersSoil, FarmersWater,
  FarmersObservationsHub,
} from "@/pages/FarmersHubs";

/**
 * Every entry follows the shape:
 *   { path, element, standalonePath?, name }
 *
 * `path` is the embedded URL (`/farmers/foo`).
 * `standalonePath` is the URL when running at farmerspulse.com root.
 *   If unset, it's derived by stripping the "/farmers" prefix.
 */
export const ROUTES = [
  { path: "/farmers", element: <FarmersPulseHome />, standalonePath: "/", name: "home" },
  { path: "/farmers/about", element: <FarmersAbout />, name: "about" },
  { path: "/farmers/mine", element: <FarmersMine />, name: "mine" },
  { path: "/farmers/weather", element: <FarmersWeather />, name: "weather" },
  { path: "/farmers/map", element: <FarmersMap />, name: "map" },
  { path: "/farmers/local", element: <FarmersLocal />, name: "local" },
  { path: "/farmers/geo-tree", element: <FarmersGeoTree />, name: "geo-tree" },
  { path: "/farmers/community", element: <FarmersCommunity />, name: "community" },
  { path: "/farmers/community/:qid", element: <FarmersCommunityDetail />, name: "community-detail" },
  { path: "/farmers/markets", element: <FarmersMarkets />, name: "markets" },
  { path: "/farmers/alerts", element: <FarmersAlerts />, name: "alerts" },
  { path: "/farmers/livestock", element: <FarmersLivestock />, name: "livestock" },
  { path: "/farmers/knowledge", element: <FarmersKnowledge />, name: "knowledge" },
  { path: "/farmers/research", element: <FarmersResearch />, name: "research" },
  { path: "/farmers/government", element: <FarmersGovernment />, name: "government" },
  { path: "/farmers/equipment", element: <FarmersEquipment />, name: "equipment" },
  { path: "/farmers/news", element: <FarmersNews />, name: "news" },
  { path: "/farmers/soil", element: <FarmersSoil />, name: "soil" },
  { path: "/farmers/water", element: <FarmersWater />, name: "water" },
  { path: "/farmers/observations", element: <FarmersObservationsHub />, name: "observations" },
  { path: "/farmers/farms", element: <FarmersFarms />, name: "farms" },
  { path: "/farmers/farms/new", element: <FarmersFarmNew />, name: "farm-new" },
  { path: "/farmers/farms/:id", element: <FarmersFarmDetail />, name: "farm-detail" },
  { path: "/farmers/report", element: <FarmersReport />, name: "report" },
  { path: "/farmers/pest-heatmap", element: <FarmersPestHeatmap />, name: "pest-heatmap" },
  { path: "/farmers/ask", element: <FarmersAsk />, name: "ask" },
  { path: "/farmers/crops", element: <FarmersCrops />, name: "crops" },
  { path: "/farmers/crops/:slug", element: <FarmersCropDetail />, name: "crop-detail" },
  { path: "/farmers/calendar", element: <FarmersCalendar />, name: "calendar" },
  { path: "/farmers/websites", element: <FarmersWebsitesList />, name: "websites" },
  { path: "/farmers/websites/new", element: <FarmersWebsiteWizard />, name: "website-new" },
  { path: "/farmers/websites/import", element: <FarmersWebsiteImport />, name: "website-import" },
  { path: "/farmers/websites/:id", element: <FarmersWebsiteEditor />, name: "website-editor" },
  { path: "/farmers/inventory", element: <FarmersInventory />, name: "inventory" },
  { path: "/farmers/almanac", element: <FarmersPulseAlmanac />, name: "almanac-full" },
  { path: "/farmers/almanac-full", element: <FarmersPulseAlmanac />, name: "almanac-editorial" },
  { path: "/f/:slug", element: <FarmerSitePublic />, standalonePath: "/f/:slug", name: "public-site" },

  // === Today dashboard ===
  { path: "/farmers/today", element: <FarmersToday />, name: "today" },

  // === Billing ===
  { path: "/farmers/billing",         element: <FarmersBilling />,        name: "billing" },
  { path: "/farmers/pricing",         element: <FarmersBilling />,        name: "pricing" },
  { path: "/farmers/billing/success", element: <FarmersBillingSuccess />, name: "billing-success" },
  { path: "/farmers/admin/revenue",   element: <FarmersRevenueAdmin />,   name: "admin-revenue" },

  // === Network ===
  { path: "/farmers/network", element: <FarmersNetwork />, name: "network" },

  // === Sell & Earn ===
  { path: "/farmers/products",   element: <FarmersProducts />,        name: "products" },
  { path: "/farmers/orders",     element: <FarmersOrders />,          name: "orders" },
  { path: "/farmers/deals",      element: <FarmersDeals />,           name: "deals" },
  { path: "/farmers/surplus",    element: <FarmersSurplus />,         name: "surplus" },
  { path: "/farmers/marketplace",        element: <FarmersMarketplaceHub />, name: "marketplace" },
  { path: "/farmers/marketplace-events", element: <FarmersMarketplace />,    name: "marketplace-events" },
  { path: "/farmers/farm-stand", element: <FarmersFarmStand />,       name: "farm-stand" },
  { path: "/farmers/buyers",     element: <FarmersBuyers />,          name: "buyers" },
  { path: "/farmers/needs",      element: <FarmersNeeds />,           name: "needs" },

  // === Visit & Experience ===
  { path: "/farmers/farm-stay",   element: <FarmersFarmStay />,    name: "farm-stay" },
  { path: "/farmers/farm-dining", element: <FarmersFarmDining />,  name: "farm-dining" },
  { path: "/farmers/experiences", element: <FarmersExperiences />, name: "experiences" },
  { path: "/farmers/events",      element: <FarmersEvents />,      name: "events" },

  // === Knowledge ===
  { path: "/farmers/learning",   element: <FarmersLearning />,   name: "learning" },
  { path: "/farmers/innovation", element: <FarmersInnovation />, name: "innovation" },

  // === Support ===
  { path: "/farmers/farm-help",    element: <FarmersFarmHelp />,     name: "farm-help" },
  { path: "/farmers/jobs",         element: <FarmersJobs />,         name: "jobs" },
  { path: "/farmers/services",     element: <FarmersServices />,     name: "services" },
  { path: "/farmers/cooperatives", element: <FarmersCooperatives />, name: "cooperatives" },
  { path: "/farmers/transport",    element: <FarmersTransport />,    name: "transport" },
  { path: "/farmers/storage",      element: <FarmersStorage />,      name: "storage" },

  // === Tools ===
  { path: "/farmers/documents", element: <FarmersDocuments />, name: "documents" },
  { path: "/farmers/finance",   element: <FarmersFinance />,   name: "finance" },
  { path: "/farmers/journal",   element: <FarmersJournal />,   name: "journal" },
  { path: "/farmers/reports",   element: <FarmersReports />,   name: "reports" },
];

/**
 * Convert an embedded path ("/farmers/mine") to its standalone equivalent
 * ("/mine"). Root ("/farmers") maps to "/".
 */
export const toStandalonePath = (path, explicit) => {
  if (explicit) return explicit;
  if (path === "/farmers") return "/";
  return path.replace(/^\/farmers/, "") || "/";
};
